import { Callout } from "@moneymap/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import type { BriefingRead } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { QueryState } from "../components/QueryState";
import { formatHouseholdDate, formatMoney } from "../lib/format";
import "./iternest-home.css";

type DashboardIconName =
  | "arrow"
  | "calendar"
  | "check"
  | "expense"
  | "grocery"
  | "income"
  | "info"
  | "more"
  | "school"
  | "shield"
  | "sparkle"
  | "transfer";

type WatchItem = {
  id: string;
  label: string;
  status: string;
  tone: "positive" | "warning" | "neutral";
  icon: DashboardIconName;
  currentMinor?: string;
  targetMinor?: string;
};

function DashboardIcon({ name, size = 24 }: { name: DashboardIconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (name) {
    case "arrow":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M5 12h13" />
          <path d="m14 7 5 5-5 5" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M8 3v4M16 3v4M3 10h18" />
          <path d="M8 14h3M13 14h3M8 18h3" />
        </svg>
      );
    case "check":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="m5 12 4 4L19 6" />
        </svg>
      );
    case "expense":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M7 3h8l4 4v14H7z" />
          <path d="M15 3v5h5M10 12h6M10 16h6" />
        </svg>
      );
    case "grocery":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M7 4v9a3 3 0 0 1-3 3" />
          <path d="M4 4v7M10 4v7" />
          <path d="M17 4c2.5 3 2.5 7 0 10v6" />
          <path d="M17 4v16" />
        </svg>
      );
    case "income":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M12 4v16M5 12h14" />
        </svg>
      );
    case "info":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5M12 8h.01" />
        </svg>
      );
    case "more":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
          <circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case "school":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="m3 10 9-5 9 5-9 5z" />
          <path d="M7 13v4c3 2 7 2 10 0v-4M21 10v6" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M12 3 5 6v5c0 4.6 2.8 8 7 10 4.2-2 7-5.4 7-10V6z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "sparkle":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M12 3c.7 4 2.3 5.6 6 6-3.7.4-5.3 2-6 6-.7-4-2.3-5.6-6-6 3.7-.4 5.3-2 6-6Z" />
          <path d="M19 15c.3 1.7 1 2.4 2.5 2.5-1.5.1-2.2.8-2.5 2.5-.3-1.7-1-2.4-2.5-2.5 1.5-.1 2.2-.8 2.5-2.5Z" />
        </svg>
      );
    case "transfer":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M4 8h14M15 5l3 3-3 3M20 16H6M9 13l-3 3 3 3" />
        </svg>
      );
  }
}

function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function firstName(displayName: string | undefined, email: string | null): string {
  const name = displayName?.trim().split(/\s+/)[0];
  if (name) return name;
  const emailName = email
    ?.split("@")[0]
    ?.replace(/[._-]+/g, " ")
    .trim();
  if (!emailName) return "there";
  return emailName.charAt(0).toUpperCase() + emailName.slice(1);
}

function isCoveredState(state: string): boolean {
  return state === "covered" || state === "paid";
}

function watchStatus(state: string): { label: string; tone: WatchItem["tone"] } {
  if (state === "covered" || state === "paid") return { label: "On track", tone: "positive" };
  if (state === "partially_covered") return { label: "Partially covered", tone: "neutral" };
  if (state === "at_risk") return { label: "Keep an eye on this", tone: "warning" };
  return { label: "Needs confirmation", tone: "warning" };
}

function iconForLabel(label: string): DashboardIconName {
  const normalized = label.toLowerCase();
  if (normalized.includes("school") || normalized.includes("tuition")) return "school";
  if (normalized.includes("grocery") || normalized.includes("food")) return "grocery";
  return "shield";
}

function progressPercent(
  currentMinor: string | undefined,
  targetMinor: string | undefined,
): number {
  if (!currentMinor || !targetMinor) return 100;
  const current = Math.abs(Number(currentMinor));
  const target = Math.abs(Number(targetMinor));
  if (!Number.isFinite(current) || !Number.isFinite(target) || target <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((current / target) * 100)));
}

function buildWatchItems(briefing: BriefingRead): WatchItem[] {
  const items: WatchItem[] = [];
  const seenIds = new Set<string>();
  const seenLabels = new Set<string>();
  const reservations = [...briefing.whatIsCovered, ...briefing.whatIsComing];

  for (const reservation of reservations) {
    const labelKey = reservation.label.toLowerCase();
    if (seenIds.has(reservation.id) || seenLabels.has(labelKey)) continue;
    const status = watchStatus(reservation.state);
    items.push({
      id: `reservation-${reservation.id}`,
      label: reservation.label,
      status: status.label,
      tone: status.tone,
      icon: iconForLabel(reservation.label),
      currentMinor: reservation.reservedMinor,
      targetMinor: reservation.amountMinor,
    });
    seenIds.add(reservation.id);
    seenLabels.add(labelKey);
    if (items.length === 3) return items;
  }

  for (const label of briefing.household.protectedPurposeLabels) {
    const labelKey = label.toLowerCase();
    if (seenLabels.has(labelKey)) continue;
    items.push({
      id: `purpose-${label}`,
      label,
      status: "Protected",
      tone: "positive",
      icon: iconForLabel(label),
    });
    seenLabels.add(labelKey);
    if (items.length === 3) return items;
  }

  if (items.length < 3) {
    items.push({
      id: "grocery-money",
      label: "Grocery money",
      status: "Available this week",
      tone: "neutral",
      icon: "grocery",
      currentMinor: briefing.household.groceriesFreeMoneyMinor,
    });
  }

  return items.slice(0, 3);
}

export function IterNestHomePage() {
  const api = useApi();
  const { email } = useAuth();
  const briefingQuery = useQuery({
    queryKey: ["briefing"],
    queryFn: () => api.getBriefing(),
    refetchInterval: 60_000,
  });
  const profileQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => api.getMe(),
    staleTime: 5 * 60_000,
  });
  const briefing = briefingQuery.data?.data;
  const profile = profileQuery.data?.data;
  const acknowledgeMutation = useMutation({
    mutationFn: (briefingId: string) => api.acknowledgeBriefing(briefingId),
  });

  useEffect(() => {
    if (
      !briefing ||
      briefing.acknowledgedAt ||
      acknowledgeMutation.isPending ||
      acknowledgeMutation.isSuccess
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => acknowledgeMutation.mutate(briefing.id), 2_500);
    return () => window.clearTimeout(timer);
  }, [briefing, acknowledgeMutation]);

  const watchItems = useMemo(() => (briefing ? buildWatchItems(briefing) : []), [briefing]);

  return (
    <div className="iternest-home">
      <h1 className="iternest-home__sr-only">Here is the household plan</h1>
      <QueryState
        isPending={briefingQuery.isPending}
        isError={briefingQuery.isError && !briefing}
        error={briefingQuery.error}
        onRetry={() => void briefingQuery.refetch()}
        loadingTitle="Opening your IterNest Money briefing"
      >
        {briefing ? (
          <HomeDashboard
            briefing={briefing}
            displayName={firstName(profile?.displayName, email)}
            watchItems={watchItems}
            isUpdating={briefingQuery.isFetching}
          />
        ) : null}
      </QueryState>
    </div>
  );
}

function HomeDashboard({
  briefing,
  displayName,
  watchItems,
  isUpdating,
}: {
  briefing: BriefingRead;
  displayName: string;
  watchItems: WatchItem[];
  isUpdating: boolean;
}) {
  const nextDue = [...briefing.whatIsComing]
    .filter((item) => item.dueDate)
    .sort((left, right) => String(left.dueDate).localeCompare(String(right.dueDate)))[0];
  const allUpcomingCovered =
    briefing.whatIsComing.length === 0 ||
    briefing.whatIsComing.every((item) => isCoveredState(item.state));
  const hasAttention = briefing.needsAttention.length > 0 || briefing.limitations.length > 0;
  const title = briefing.freshness.isStale
    ? "Your plan needs a refresh."
    : hasAttention
      ? "A few things need attention."
      : allUpcomingCovered
        ? "You’re in good shape."
        : "Your plan is taking shape.";
  const summary = briefing.freshness.isStale
    ? "This is the last trustworthy view while IterNest Money checks for newer information."
    : hasAttention
      ? "Most of your plan is protected, but a few details could change the recommendation."
      : "Bills are covered, your essentials are protected, and your plan is on track.";
  const attentionMessage = briefing.needsAttention[0]?.message;
  const changedMessage = briefing.whatChanged[0]?.message;
  const activityMessage = briefing.household.notableActivity[0];
  const thirdLine = attentionMessage ?? changedMessage ?? activityMessage;
  const thirdLabel = attentionMessage
    ? "Something needs your attention"
    : changedMessage
      ? "Your plan was updated"
      : activityMessage
        ? "Recent activity checked"
        : "Your safe-to-use amount is ready";

  return (
    <>
      <section className="iternest-hero" aria-labelledby="iternest-home-status">
        <div className="iternest-hero__glow" aria-hidden="true" />
        <img className="iternest-hero__orchid" src="/orchid.svg" alt="" aria-hidden="true" />
        <div className="iternest-hero__content">
          <p className="iternest-hero__eyebrow">
            {greetingForHour(new Date().getHours())}, {displayName}{" "}
            <DashboardIcon name="sparkle" size={15} />
          </p>
          <h2 id="iternest-home-status">{title}</h2>
          <p className="iternest-hero__summary">{summary}</p>
          <div className="iternest-hero__checks">
            <div className="iternest-hero__check">
              <span className="iternest-hero__check-icon">
                <DashboardIcon name={allUpcomingCovered ? "check" : "info"} size={22} />
              </span>
              <span>
                <strong>
                  {allUpcomingCovered ? "Bills are covered" : "Bills need a closer look"}
                </strong>
                <small>
                  {nextDue?.dueDate
                    ? `Next due: ${formatHouseholdDate(nextDue.dueDate)}`
                    : "No immediate bills are due"}
                </small>
              </span>
            </div>
            <div className="iternest-hero__check">
              <span className="iternest-hero__check-icon">
                <DashboardIcon name="check" size={22} />
              </span>
              <span>
                <strong>Grocery money available</strong>
                <small>
                  {formatMoney(briefing.household.groceriesFreeMoneyMinor, briefing.currency)} left
                  this week
                </small>
              </span>
            </div>
            <div className="iternest-hero__check">
              <span className="iternest-hero__check-icon iternest-hero__check-icon--info">
                <DashboardIcon name="info" size={22} />
              </span>
              <span>
                <strong>{thirdLabel}</strong>
                <small>
                  {thirdLine ??
                    `${formatMoney(briefing.displaySafeToUseMinor, briefing.currency)} remains after known commitments.`}
                </small>
              </span>
            </div>
          </div>
          <Link className="iternest-hero__button" to="/coverage">
            <DashboardIcon name="sparkle" size={18} />
            View full briefing
          </Link>
          <p className="iternest-hero__freshness" aria-live="polite">
            {isUpdating
              ? "Refreshing your plan…"
              : briefing.freshness.isStale
                ? "Last trustworthy calculation"
                : "Current calculation"}
          </p>
        </div>
      </section>

      {briefing.freshness.isStale ? (
        <Callout title="This is the last trustworthy plan" tone="warning">
          IterNest Money has paused recommendations that depend on newer balances.
        </Callout>
      ) : null}

      {briefing.limitations.length > 0 ? (
        <Callout title="What this plan cannot confirm yet" tone="warning">
          <ul>
            {briefing.limitations.map((limitation) => (
              <li key={limitation}>{limitation}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <section className="iternest-recommendation" aria-labelledby="today-recommendation-title">
        <div className="iternest-recommendation__header">
          <div>
            <p className="iternest-section-eyebrow">Today’s recommendation</p>
            <h2 id="today-recommendation-title">{briefing.todayAction.action}</h2>
          </div>
          <span className="iternest-recommendation__icon" aria-hidden="true">
            <DashboardIcon
              name={
                briefing.todayAction.type === "wait" || briefing.todayAction.type === "do_nothing"
                  ? "calendar"
                  : "shield"
              }
              size={31}
            />
          </span>
        </div>
        <div className="iternest-recommendation__reason">
          <p>Reason</p>
          <span>{briefing.todayAction.whyNow || briefing.todayAction.reason}</span>
        </div>
        <Link className="iternest-detail-link" to={`/action/${briefing.todayAction.id}`}>
          See details <DashboardIcon name="arrow" size={17} />
        </Link>
      </section>

      <section className="iternest-watch" aria-labelledby="watch-title">
        <div className="iternest-section-heading">
          <p className="iternest-section-eyebrow" id="watch-title">
            You’re watching
          </p>
          <Link to="/coverage">View all</Link>
        </div>
        <div className="iternest-watch__grid">
          {watchItems.map((item) => {
            const percent = progressPercent(item.currentMinor, item.targetMinor);
            return (
              <Link
                className={`iternest-watch-card iternest-watch-card--${item.tone}`}
                to="/coverage"
                key={item.id}
              >
                <span className="iternest-watch-card__icon">
                  <DashboardIcon name={item.icon} size={29} />
                </span>
                <strong>{item.label}</strong>
                <span>{item.status}</span>
                <div
                  className="iternest-progress"
                  role="progressbar"
                  aria-label={`${item.label} is ${percent}% funded`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                >
                  <span style={{ width: `${percent}%` }} />
                </div>
                <small>
                  {item.currentMinor && item.targetMinor
                    ? `${formatMoney(item.currentMinor, briefing.currency)} of ${formatMoney(item.targetMinor, briefing.currency)}`
                    : item.currentMinor
                      ? formatMoney(item.currentMinor, briefing.currency)
                      : "Included in the protected plan"}
                </small>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="iternest-quick-actions" aria-labelledby="quick-actions-title">
        <p className="iternest-section-eyebrow" id="quick-actions-title">
          Quick actions
        </p>
        <div className="iternest-quick-actions__grid">
          <Link to="/activity">
            <span>
              <DashboardIcon name="income" size={27} />
            </span>
            Add income
          </Link>
          <Link to="/activity">
            <span>
              <DashboardIcon name="expense" size={27} />
            </span>
            Add expense
          </Link>
          <Link to="/plan">
            <span>
              <DashboardIcon name="transfer" size={27} />
            </span>
            Transfer
          </Link>
          <Link to="/settings">
            <span>
              <DashboardIcon name="more" size={27} />
            </span>
            More
          </Link>
        </div>
      </section>
    </>
  );
}
